// addIndex — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-index', index })` op (DSE2 §3.C1–§3.C4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice C1 — Filtered / partial index UI.
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice C2 — Covering / INCLUDE columns UI.
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice C3 — Expression / functional index UI.
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice C4 — Specialized index types UI.
//
// Per-engine support (mirrors DSE2's `synthesizeCreateIndex` rules):
//   - Add-index itself is supported on every engine.
//   - `partialWhereText` (filtered / partial index `WHERE …` clause) is
//     supported on mssql (2008+), postgres, sqlite. mysql + oracle reject
//     with a redirect to the v2 Slice C3 functional-index workaround.
//   - `includeColumns` (covering / INCLUDE) is supported on mssql (2005+)
//     and postgres (11+). mysql / oracle / sqlite reject — there is no
//     portable workaround (moving the columns into the key list changes
//     the index's sort order + uniqueness scope).
//   - Expression / functional-index entries (`{ kind: 'expression', text }`)
//     are accepted on postgres / mysql / oracle / sqlite. mssql rejects —
//     mssql 2022 introduced experimental support but Jam's supported-engine
//     matrix does not gate on it; the documented workaround is to add a
//     PERSISTED computed column (Slice A3) and index the column by name.
//   - `indexType` (Slice C4): per-engine support map mirrors DSE2's
//     `ENGINE_INDEX_TYPE_KINDS` table — `getIndexTypeKindsForEngine(engine)`
//     returns the supported kind set. The dropdown UI hides unsupported
//     kinds; `validateIndexForEngine` rejects the rest pre-flight so the
//     synthesizer never throws from this surface.
//
// Reuses `parseColumnList` from `addPrimaryKey` for comma-separated column input.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  IndexColumn,
  IndexDef,
  IndexType,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddIndexArgs = {
  readonly index: IndexDef;
};

export function buildAddIndexEdit(args: AddIndexArgs): DdlEdit {
  return { op: 'add-index', index: args.index };
}

export async function commitAddIndex(
  ctx: VqeDdlEditContext,
  args: AddIndexArgs,
): Promise<void> {
  const edit = buildAddIndexEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAddIndexSupported(_engine: SqlEngine): boolean {
  void _engine;
  return true;
}

/** Engines that support a partial / filtered index `WHERE …` clause on
 *  CREATE INDEX. DSE2 enforces — this predicate mirrors the synthesizer's
 *  `rejectPartialIndexIfUnsupported`. */
export function isFilteredIndexSupported(engine: SqlEngine): boolean {
  return engine === 'mssql' || engine === 'postgres' || engine === 'sqlite';
}

export function getFilteredIndexUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL CREATE INDEX has no WHERE clause. Use a functional index '
      + 'instead: index a CASE expression that '
      + 'returns the key when the predicate matches and NULL otherwise.'
    );
  }
  if (engine === 'oracle') {
    return (
      'Oracle CREATE INDEX has no WHERE clause. Use a function-based '
      + 'index instead: index a CASE expression that '
      + 'returns the key when the predicate matches and NULL otherwise — '
      + 'rows where the CASE is NULL are excluded from the index.'
    );
  }
  return '';
}

/** Engines that support `INCLUDE (cols)` / covering columns on CREATE INDEX
 *  (v2 Slice C2). DSE2 enforces — this predicate mirrors the synthesizer's
 *  `rejectIncludeColumnsIfUnsupported`. Only mssql (2005+) and postgres
 *  (11+) accept the clause. */
export function isIncludeColumnsSupported(engine: SqlEngine): boolean {
  return engine === 'mssql' || engine === 'postgres';
}

export function getIncludeColumnsUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL CREATE INDEX has no INCLUDE clause. There is no portable '
      + 'workaround — moving the requested columns into the key column list '
      + 'changes the index sort order and uniqueness scope.'
    );
  }
  if (engine === 'oracle') {
    return (
      'Oracle CREATE INDEX has no INCLUDE clause. There is no portable '
      + 'workaround — moving the requested columns into the key column list '
      + 'changes the index sort order and uniqueness scope.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite CREATE INDEX has no INCLUDE clause. There is no portable '
      + 'workaround — moving the requested columns into the key column list '
      + 'changes the index sort order and uniqueness scope.'
    );
  }
  return '';
}

/** Engines that accept direct expression / functional-index entries on
 *  CREATE INDEX (v2 Slice C3). DSE2 enforces — this predicate mirrors the
 *  synthesizer's `rejectExpressionIndexIfUnsupported`. mssql is the only
 *  engine gated out: mssql 2022 introduced experimental support but Jam's
 *  supported-engine matrix does not gate on it; the documented workaround is
 *  the PERSISTED computed-column-then-index pattern (Slice A3). */
export function isExpressionIndexSupported(engine: SqlEngine): boolean {
  return engine !== 'mssql';
}

export function getExpressionIndexUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'SQL Server CREATE INDEX cannot index an expression directly. '
      + 'Add a PERSISTED computed column that computes the expression, '
      + 'then index the computed column by name.'
    );
  }
  return '';
}

/** True iff at least one entry in `columns` is the discriminated
 *  `{ kind: 'expression', text }` shape. A plain string is never counted, and
 *  that is deliberate even though a string MAY carry an expression: this gate
 *  exists to refuse a functional index on an engine that cannot run one, and
 *  refusing every string entry would refuse `'a DESC'` too. A string that is a
 *  column reference is named downstream (`existingNameEvidence.ts`'s
 *  `decodeTypedIndexColumnRef`); one that is not keeps its bytes and reaches
 *  the server as written, which is where an unsupported expression is caught. */
export function hasExpressionColumn(
  columns: readonly (string | IndexColumn)[],
): boolean {
  for (const c of columns) {
    if (typeof c === 'string') continue;
    if (c.kind === 'expression') return true;
  }
  return false;
}

/** v2 Slice C4 — Specialized index-type kind set per engine. Mirrors DSE2's
 *  `ENGINE_INDEX_TYPE_KINDS` (apply/dialect.index.ts §Slice C4). The form
 *  uses this list to drive the Type dropdown options — kinds absent from the
 *  list are hidden so the user cannot pick an option the synthesizer would
 *  reject. `'btree'` is always present and represents the engine default. */
const ENGINE_INDEX_TYPE_KINDS: Record<SqlEngine, readonly IndexType['kind'][]> = {
  mssql: ['btree', 'columnstore', 'xml', 'spatial', 'fulltext'],
  postgres: ['btree', 'hash', 'gin', 'gist', 'brin', 'spgist'],
  mysql: ['btree', 'hash', 'fulltext', 'spatial'],
  oracle: ['btree', 'bitmap', 'domain'],
  sqlite: ['btree'],
};

/** Returns the supported `IndexType.kind` set for `engine`. The form uses this
 *  to populate the Type dropdown options; kinds absent from the returned list
 *  are hidden so users can never pick an unsupported combination. */
export function getIndexTypeKindsForEngine(
  engine: SqlEngine,
): readonly IndexType['kind'][] {
  return ENGINE_INDEX_TYPE_KINDS[engine];
}

/** True iff `engine` accepts the named `IndexType.kind`. Returns true for
 *  any kind in the engine's `ENGINE_INDEX_TYPE_KINDS` set; false otherwise. */
export function isIndexTypeSupported(
  engine: SqlEngine,
  kind: IndexType['kind'],
): boolean {
  return ENGINE_INDEX_TYPE_KINDS[engine].includes(kind);
}

export function getIndexTypeUnsupportedTooltip(
  engine: SqlEngine,
  kind: IndexType['kind'],
): string {
  if (isIndexTypeSupported(engine, kind)) return '';
  const supported = ENGINE_INDEX_TYPE_KINDS[engine].join(', ');
  return (
    `${engine} does not support index type '${kind}'. Supported on ${engine}: `
    + `${supported}.`
  );
}

/** True iff sqlite is the engine — sqlite supports only the default b-tree
 *  index type, so the Type dropdown has nothing meaningful to choose. The
 *  form hides the dropdown entirely on sqlite (rather than rendering a
 *  single-option dropdown). */
export function isIndexTypeDropdownMeaningful(engine: SqlEngine): boolean {
  return ENGINE_INDEX_TYPE_KINDS[engine].length > 1;
}

/** Pre-flight validator mirroring DSE2's `synthesizeCreateIndex` reject
 *  paths so the form can surface engine errors before the synthesizer
 *  would throw. Returns null when the spec is acceptable. */
export function validateIndexForEngine(
  engine: SqlEngine,
  index: IndexDef,
): string | null {
  if (index.columns.length === 0) {
    return 'CREATE INDEX requires at least one column.';
  }
  if (hasExpressionColumn(index.columns) && !isExpressionIndexSupported(engine)) {
    return getExpressionIndexUnsupportedTooltip(engine);
  }
  const hasFilter
    = index.partialWhereText !== undefined
      && index.partialWhereText.length > 0;
  if (hasFilter && !isFilteredIndexSupported(engine)) {
    return getFilteredIndexUnsupportedTooltip(engine);
  }
  const hasInclude
    = index.includeColumns !== undefined
      && index.includeColumns.length > 0;
  if (hasInclude && !isIncludeColumnsSupported(engine)) {
    return getIncludeColumnsUnsupportedTooltip(engine);
  }
  if (index.indexType !== undefined) {
    if (!isIndexTypeSupported(engine, index.indexType.kind)) {
      return getIndexTypeUnsupportedTooltip(engine, index.indexType.kind);
    }
    if (engine === 'mssql' && index.indexType.kind === 'fulltext') {
      const keyIndex = index.indexType.mssqlKeyIndex;
      if (!keyIndex || keyIndex.length === 0) {
        return (
          'mssql FULLTEXT INDEX requires a Key Index — mssql\'s CREATE FULLTEXT '
          + 'INDEX syntax has a mandatory `KEY INDEX <name>` clause naming the '
          + 'unique key index used to identify rows in the fulltext catalog.'
        );
      }
    }
    if (engine === 'oracle' && index.indexType.kind === 'domain') {
      const indextype = index.indexType.indextype;
      if (!indextype || indextype.length === 0) {
        return (
          'oracle DOMAIN INDEX requires an `INDEXTYPE IS …` clause — pass the '
          + 'domain-index type (e.g. `CTXSYS.CONTEXT` for Oracle Text, '
          + '`MDSYS.SPATIAL_INDEX` for Oracle Spatial).'
        );
      }
    }
  }
  return null;
}

export { parseColumnList } from './addPrimaryKey';
